"use client";

import { Card, CircularProgress } from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetCustomerSalesProductPayments from "@/hooks/requests/customer-sales-product-payments/use-get-customer-sales-product-payments";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { ICustomerSalesProductPayment } from "@/interfaces/customer-sales-product-payment.interfaces";
import { CompanyPaymentsMessages } from "@/messages/customer/company-payments.messages";

import { CompanyPaymentsTable } from "./components/Table";

type Props = {
  messages: CompanyPaymentsMessages;
};

export default function CompanyPaymentsPage({ messages }: Props) {
  const { lang } = useLanguage();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const filter = useFilters<ICustomerSalesProductPayment>({ initialValue: [] });
  const filters = filter.filters;
  const { data, error, isLoading } = useGetCustomerSalesProductPayments({
    filters,
    pageNumber,
    pageSize,
  });

  const shouldLoadPaymentsTable = !!data && data.items.length > 0 && !isLoading;
  const shouldLoadError = !data && !!error && !isLoading;
  const shouldLoadNoPaymentsFound = !!data && data.items.length === 0 && !error && !isLoading;

  const BREAD_CRUMBS = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.companyPayments(lang) },
    {
      title: "Empresa",
      href: ROUTE_URLS.customerSubscriptionTab(lang),
    },
  ];

  return (
    <PageLayout messages={messages} title={messages.pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(16) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              {shouldLoadPaymentsTable && (
                <CompanyPaymentsTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.paymentErrorToLoad}
                  description={error.message}
                  alt={messages.paymentNotFoundAlt}
                />
              )}

              {shouldLoadNoPaymentsFound && (
                <AlertBlock
                  title={messages.paymentNotFoundError}
                  description={messages.paymentNotFoundDescription}
                  alt={messages.paymentNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
